import { useContext, useState, useRef, useEffect } from "react";
import { useLoaderData, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import WizardPopup from "@/components/shared/WizardPopup";
import { useConfirmDialogFocus } from "@/components/shared/useConfirmDialogFocus";
import { User, UserOrg, UserOrgForm } from "@/models/user.tsx";
import {
  UserWizardSearchForm,
  UserSearchFormRef,
  UserSearchFormState,
} from "./UserWizardSearchForm";
import {
  UserWizardSearchResults,
  UserSearchResultsRef,
} from "./UserWizardSearchResults";
import { UserAdminData } from "@/pages/UserAdminPage";
import { GrantAccessForm } from "../GrantAccessForm";
import LoaderButton from "@/components/shared/LoaderButton";
import { fetchAuthenticatedData } from "@/queries/shared";
import { GlobalStateModel } from "@/models";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import {
  buildUsernameParts,
  isUsernameTaken,
  sanitizeUsernamePrefix,
  USERNAME_DOMAIN,
} from "@/utils/username";

type AddUserWizardProps = {
  show: boolean;
  onClose: () => void;
  allUsers: User<UserOrg>[];
  isStateLevel?: boolean;
  leaid?: string;
  vendorNumber?: string | number;
  coreData?: any;
  entityResult?: any;
  childrenResult?: any;
};

const newUserTemplate: User<UserOrg> = {
  userName: "",
  email: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  phoneExtension: "",
  userOrgs: [],
};

type ConfirmModalProps = {
  titleId: string;
  title: string;
  message: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
};

function ConfirmModal({
  titleId,
  title,
  message,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const handleKeyDown = useConfirmDialogFocus(cancelRef, confirmRef, onCancel);

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };
  const boxStyle: React.CSSProperties = {
    background: "white",
    padding: "32px 40px",
    borderRadius: "8px",
    maxWidth: "480px",
    width: "90%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
  };
  const btnBase: React.CSSProperties = {
    padding: "8px 20px",
    borderRadius: "4px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: "500",
    border: "none",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={`${titleId}-message`}
      onKeyDown={handleKeyDown}
      style={overlayStyle}
    >
      <div style={boxStyle}>
        <h5
          id={titleId}
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            margin: "0 0 12px",
          }}
        >
          {title}
        </h5>
        <p
          id={`${titleId}-message`}
          style={{
            fontSize: "14px",
            color: "#666",
            margin: "0 0 24px",
            lineHeight: "1.5",
          }}
        >
          {message}
        </p>
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
        >
          <button
            ref={cancelRef}
            onClick={onCancel}
            style={{
              ...btnBase,
              background: "white",
              border: "1px solid #ddd",
              color: "#333",
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            style={{ ...btnBase, background: "#1e5a78", color: "white" }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AddUserWizard({
  show,
  onClose,
  allUsers,
  isStateLevel = true,
  leaid = "",
  vendorNumber = "",
  coreData,
  entityResult,
  childrenResult,
}: AddUserWizardProps) {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const globalState = useLoaderData({ from: "__root__" }) as GlobalStateModel;
  const { showToast } = useContext(FileSubmissionsContext);

  const [currentStep, setCurrentStep] = useState(isStateLevel ? 2 : 1);
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<User<UserOrg>[]>([]);
  const [searchCriteria, setSearchCriteria] =
    useState<UserSearchFormState | null>(null);
  const [searchError, setSearchError] = useState<string | undefined>(undefined);
  const [selectedUser, setSelectedUser] =
    useState<User<UserOrg>>(newUserTemplate);
  const [isNewRecord, setIsNewRecord] = useState(true);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showBackToSearchConfirm, setShowBackToSearchConfirm] = useState(false);

  const [usernamePrefix, setUsernamePrefix] = useState("");
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
  const [usernameError, setUsernameError] = useState<string | undefined>(
    undefined
  );

  const [pendingUserOrg, setPendingUserOrg] = useState<UserOrgForm | null>(
    null
  );

  const lockedSuffix = `${vendorNumber ?? ""}${USERNAME_DOMAIN}`;

  const searchFormRef = useRef<UserSearchFormRef>(null);
  const searchResultsRef = useRef<UserSearchResultsRef>(null);
  const submitTriggerRef = useRef<(() => void) | null>(null);
  const isClosingRef = useRef(false);

  useEffect(() => {
    const modalBody = document.querySelector(".add-user-wizard .modal-body");
    if (modalBody) modalBody.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep, showResults]);

  useEffect(() => {
    if (currentStep !== 2) {
      submitTriggerRef.current = null;
      setIsFormDirty(false);
    }
  }, [currentStep]);

  useEffect(() => {
    if (isStateLevel) return;
    if (!isNewRecord || usernameManuallyEdited) return;
    const { editablePrefix } = buildUsernameParts(
      selectedUser.firstName,
      selectedUser.lastName,
      vendorNumber
    );
    setUsernamePrefix(editablePrefix);
  }, [
    selectedUser.firstName,
    selectedUser.lastName,
    isNewRecord,
    usernameManuallyEdited,
    vendorNumber,
    isStateLevel,
  ]);

  useEffect(() => {
    if (isStateLevel) return;
    if (!isNewRecord) return;
    const full = `${usernamePrefix}${lockedSuffix}`;
    setSelectedUser((p) =>
      p.userName === full ? p : { ...p, userName: full }
    );
  }, [usernamePrefix, lockedSuffix, isNewRecord, isStateLevel]);

  const createUserMutation = useMutation({
    mutationFn: async (payload: User<UserOrg | UserOrgForm>) => {
      const intendedUserOrgs = payload.userOrgs;
      const profilePayload: Record<string, any> = {
        ...payload,
        userOrgs: [] as UserOrg[],
      };
      const optionalFields = ["phoneNumber", "phoneExtension"];
      for (const field of optionalFields) {
        if (!profilePayload[field] || !String(profilePayload[field]).trim()) {
          delete profilePayload[field];
        }
      }

      const profileResponse = await fetchAuthenticatedData<
        User<UserOrg> | { success: boolean; message: string | null }
      >(globalState, `/json/account/${leaid}/users/${payload.userName}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profilePayload),
      });

      if (
        profileResponse &&
        typeof profileResponse === "object" &&
        "success" in profileResponse &&
        (profileResponse as any).success === false
      ) {
        const msg =
          (profileResponse as any).message ||
          "Server rejected the request without an error message.";
        throw new Error(msg);
      }

      if (intendedUserOrgs && intendedUserOrgs.length > 0) {
        const orgsResponse = await fetchAuthenticatedData<
          User<UserOrg> | { success: boolean; message: string | null }
        >(globalState, `/json/account/${leaid}/users/${payload.userName}/user-orgs`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...payload, userOrgs: intendedUserOrgs }),
        });

        if (
          orgsResponse &&
          typeof orgsResponse === "object" &&
          "success" in orgsResponse &&
          (orgsResponse as any).success === false
        ) {
          const msg =
            (orgsResponse as any).message ||
            "User was created but permissions could not be saved.";
          throw new Error(msg);
        }
      }

      return profileResponse as User<UserOrg>;
    },
    onSuccess: (saved) => {
      showToast("success", "Success", "User created successfully.");
      const savedUserName =
        (saved as User<UserOrg>)?.userName ?? selectedUser.userName ?? "";
      handleSaveSuccess(savedUserName);
    },
    onError: (error: any) => {
      console.error("Create user error:", error);
      showToast(
        "error",
        "Failed to create user",
        error?.message || "Please try again."
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users", leaid] });
      queryClient.invalidateQueries({ queryKey: ["users", "0-"] });
    },
  });

  useEffect(() => {
    if (isNewRecord && !isStateLevel) {
      setIsSubmitting(createUserMutation.isPending);
    }
  }, [createUserMutation.isPending, isNewRecord, isStateLevel]);

  const searchUsersMutation = useMutation({
    mutationFn: async (criteria: UserSearchFormState) => {
      const params = new URLSearchParams();
      if (criteria.username.trim()) params.set("userName", criteria.username.trim());
      if (criteria.firstName.trim()) params.set("firstName", criteria.firstName.trim());
      if (criteria.lastName.trim()) params.set("lastName", criteria.lastName.trim());
      if (criteria.email.trim()) params.set("email", criteria.email.trim());
      return fetchAuthenticatedData<User<UserOrg>[]>(
        globalState,
        `/json/account/${leaid}/search?${params.toString()}`
      );
    },
    onSuccess: (results) => {
      setSearchResults(results);
      setShowResults(true);
    },
    onError: (error: any) => {
      setSearchError(error?.message || "Search failed. Please try again.");
    },
  });

  const resetWizard = () => {
    setCurrentStep(isStateLevel ? 2 : 1);
    setShowResults(false);
    setSearchResults([]);
    setSearchCriteria(null);
    setSearchError(undefined);
    setSelectedUser(newUserTemplate);
    setIsNewRecord(true);
    setIsFormDirty(false);
    setIsSubmitting(false);
    setUsernamePrefix("");
    setUsernameManuallyEdited(false);
    setUsernameError(undefined);
    setPendingUserOrg(null);
    isClosingRef.current = false;
    submitTriggerRef.current = null;
  };

  const handleSearch = (criteria: UserSearchFormState) => {
    setSearchCriteria(criteria);
    setSearchError(undefined);
    searchUsersMutation.mutate(criteria);
  };

  const handleSelectUser = (user: User<UserOrg>) => {
    setSelectedUser(user);
    setIsNewRecord(false);
    setIsFormDirty(false);
    setUsernameManuallyEdited(true);
    setUsernameError(undefined);
    setPendingUserOrg(null);
    setCurrentStep(2);
  };

  const handleCreateNew = () => {
    const rawSearchedUsername = (searchCriteria?.username || "").trim();
    const searchedPrefix = rawSearchedUsername
      ? sanitizeUsernamePrefix(
          rawSearchedUsername.replace(/@capsdac\.org$/i, "")
        )
      : "";

    setSelectedUser({
      ...newUserTemplate,
      userName: "",
      firstName: searchCriteria?.firstName || "",
      lastName: searchCriteria?.lastName || "",
      email: searchCriteria?.email || "",
    });
    setUsernamePrefix(searchedPrefix);
    setUsernameManuallyEdited(!!searchedPrefix);
    setUsernameError(undefined);
    setPendingUserOrg(null);
    setIsNewRecord(true);
    setIsFormDirty(false);
    setCurrentStep(2);
  };

  const handleSaveSuccess = (savedUserName: string) => {
    isClosingRef.current = true;
    if (!params.leaid || !savedUserName) {
      console.error("Cannot navigate: leaid or userName is missing");
      resetWizard();
      onClose();
      return;
    }
    navigate({
      to: "/$leaid/admin/users/$userid",
      params: { leaid: params.leaid, userid: savedUserName },
    });
    resetWizard();
    onClose();
  };

  const handleCloseAttempt = () => {
    if (currentStep >= 2 && isFormDirty) {
      setShowExitConfirm(true);
    } else {
      handleCancel();
    }
  };

  const handleCancel = () => {
    resetWizard();
    onClose();
  };

  const handleBack = () => {
    if (currentStep === 2) {
      if (isFormDirty) {
        setShowBackToSearchConfirm(true);
      } else {
        setSelectedUser(newUserTemplate);
        setIsNewRecord(true);
        setUsernamePrefix("");
        setUsernameManuallyEdited(false);
        setUsernameError(undefined);
        setPendingUserOrg(null);
        setCurrentStep(isStateLevel ? 2 : 1);
        setShowResults(true);
      }
    }
  };

  const handleNewLeaUserSave = () => {
    if (createUserMutation.isPending) return;

    if (!usernamePrefix.trim()) {
      setUsernameError("Username is required.");
      return;
    }
    if (!selectedUser.userName?.trim()) {
      showToast("error", "Error", "Username is required.");
      return;
    }
    if (isUsernameTaken(selectedUser.userName, allUsers)) {
      setUsernameError(
        `"${selectedUser.userName}" is already taken. Try a different prefix.`
      );
      return;
    }

    const payload: User<UserOrg | UserOrgForm> = {
      ...selectedUser,
      userOrgs: pendingUserOrg ? [pendingUserOrg] : [],
    };
    createUserMutation.mutate(payload);
  };

  const renderFooter = () => {
    if (currentStep === 1 && showResults) {
      return (
        <div className="d-flex justify-content-between w-100">
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              searchResultsRef.current?.handleBack();
              setShowResults(false);
            }}
          >
            Previous
          </button>
          <div />
        </div>
      );
    }

    if (currentStep === 1) {
      return (
        <div className="d-flex justify-content-between w-100">
          <div />
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={() => searchFormRef.current?.handleCancel()}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              style={{ backgroundColor: "#1e5a78", borderColor: "#1e5a78" }}
              disabled={searchUsersMutation.isPending}
              onClick={() => searchFormRef.current?.handleSearch()}
            >
              {searchUsersMutation.isPending ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      );
    }

    const useNewLeaUserFlow = !isStateLevel && isNewRecord;
    const canSubmit =
      useNewLeaUserFlow || (isNewRecord && isStateLevel) || isFormDirty;

    return (
      <div className="d-flex justify-content-between w-100">
        {isStateLevel ? (
          <button className="btn btn-outline-secondary" onClick={handleCancel}>
            Cancel
          </button>
        ) : (
          <button className="btn btn-outline-secondary" onClick={handleBack}>
            Previous
          </button>
        )}
        <LoaderButton
          className="d-flex align-items-center gap-2"
          style={{
            backgroundColor: "#1e5a78",
            borderColor: "#1e5a78",
            opacity: canSubmit ? 1 : 0.5,
            cursor: canSubmit ? "pointer" : "not-allowed",
          }}
          disabled={!canSubmit}
          loading={isSubmitting}
          loadingText={
            !isStateLevel
              ? isNewRecord
                ? "Creating..."
                : "Granting..."
              : isNewRecord
                ? "Creating..."
                : "Updating..."
          }
          onClick={() => {
            if (useNewLeaUserFlow) {
              handleNewLeaUserSave();
            } else {
              submitTriggerRef.current?.();
            }
          }}
        >
          <i className="bi bi-floppy" style={{ fontSize: "14px" }} />
          {!isStateLevel
            ? isNewRecord
              ? "Create User"
              : "Grant Access"
            : isNewRecord
              ? "Create User"
              : "Update Profile"}
        </LoaderButton>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .add-user-wizard .wizard-profile-form .profile-form-actions { display: none !important; }
      `}</style>
      <WizardPopup
        show={show}
        header={
          <>
            <i className="bi bi-person-plus" /> Add User
          </>
        }
        size="xl"
        onClose={handleCloseAttempt}
        enforceFocus={!showExitConfirm && !showBackToSearchConfirm}
        hasButton={false}
        className="add-user-wizard"
        backdrop="static"
        footer={renderFooter()}
      >
        <div style={{ background: "white", padding: "24px 0 80px" }}>
          {currentStep === 1 && !showResults && (
            <UserWizardSearchForm
              ref={searchFormRef}
              disabled={false}
              onSearch={handleSearch}
              onCancel={handleCancel}
              searchError={searchError}
              onClearSearchError={() => setSearchError(undefined)}
              searchCriteria={searchCriteria}
            />
          )}

          {currentStep === 1 && showResults && (
            <UserWizardSearchResults
              ref={searchResultsRef}
              searchResults={searchResults}
              usersLoading={searchUsersMutation.isPending}
              onSelectUser={handleSelectUser}
              onCreateNew={handleCreateNew}
              onBack={() => setShowResults(false)}
            />
          )}

          {currentStep === 2 && (
            <div className="wizard-profile-form" style={{ padding: "0 24px" }}>
              {isStateLevel ? (
                <>
                  {!isNewRecord && (
                    <div
                      style={{
                        padding: "10px 16px",
                        background: "#e8f4fd",
                        border: "1px solid #b8d9f5",
                        borderRadius: "4px",
                        marginBottom: "20px",
                        fontSize: "14px",
                        color: "#1e5a78",
                      }}
                    >
                      Pre-filled from existing record for{" "}
                      <strong>
                        {selectedUser.userName || "selected user"}
                      </strong>
                      . Review and update as needed, then save.
                    </div>
                  )}
                  <UserAdminData
                    user={selectedUser}
                    hideActions={true}
                    onSaveSuccess={handleSaveSuccess}
                    onRegisterSubmit={(fn) => {
                      submitTriggerRef.current = fn;
                    }}
                    onDirtyChange={(dirty) => setIsFormDirty(dirty)}
                    onSubmittingChange={(submitting) => {
                      if (!isClosingRef.current) setIsSubmitting(submitting);
                    }}
                  />
                </>
              ) : (
                <>
                  <div className="row g-4 mb-2">
                    <div className="col-12 col-md-4">
                      <label
                        htmlFor="add-user-username"
                        className="form-label fw-semibold small"
                      >
                        Username
                      </label>
                      {isNewRecord ? (
                        <>
                          <div className="input-group">
                            <input
                              id="add-user-username"
                              className={`form-control${
                                usernameError ? " is-invalid" : ""
                              }`}
                              value={usernamePrefix}
                              onChange={(e) => {
                                setUsernamePrefix(
                                  sanitizeUsernamePrefix(e.target.value)
                                );
                                setUsernameManuallyEdited(true);
                                if (usernameError) setUsernameError(undefined);
                              }}
                              style={{
                                borderRadius: "0.375rem 0 0 0.375rem",
                              }}
                            />
                            <span
                              className="input-group-text"
                              style={{
                                background: "#dfe3e7",
                                color: "#212529",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                              }}
                              title="This portion is fixed and cannot be changed."
                            >
                              {lockedSuffix}
                            </span>
                          </div>
                          {usernameError && (
                            <div
                              style={{
                                marginTop: "6px",
                                fontSize: "13px",
                                color: "#842029",
                              }}
                            >
                              {usernameError}
                            </div>
                          )}
                        </>
                      ) : (
                        <input
                          id="add-user-username"
                          className="form-control"
                          value={selectedUser.userName || ""}
                          readOnly
                          style={{ borderRadius: "0.375rem" }}
                        />
                      )}
                    </div>
                    <div className="col-12 col-md-4">
                      <label
                        htmlFor="add-user-first-name"
                        className="form-label fw-semibold small"
                      >
                        First Name
                      </label>
                      <input
                        id="add-user-first-name"
                        className="form-control"
                        value={selectedUser.firstName || ""}
                        readOnly={!isNewRecord}
                        onChange={(e) =>
                          setSelectedUser((p) => ({
                            ...p,
                            firstName: e.target.value,
                          }))
                        }
                        style={{ borderRadius: "0.375rem" }}
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <label
                        htmlFor="add-user-last-name"
                        className="form-label fw-semibold small"
                      >
                        Last Name
                      </label>
                      <input
                        id="add-user-last-name"
                        className="form-control"
                        value={selectedUser.lastName || ""}
                        readOnly={!isNewRecord}
                        onChange={(e) =>
                          setSelectedUser((p) => ({
                            ...p,
                            lastName: e.target.value,
                          }))
                        }
                        style={{ borderRadius: "0.375rem" }}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label
                        htmlFor="add-user-email"
                        className="form-label fw-semibold small"
                      >
                        Email Address
                      </label>
                      <input
                        id="add-user-email"
                        className="form-control"
                        value={selectedUser.email || ""}
                        readOnly={!isNewRecord}
                        onChange={(e) =>
                          setSelectedUser((p) => ({
                            ...p,
                            email: e.target.value,
                          }))
                        }
                        style={{ borderRadius: "0.375rem" }}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label
                        htmlFor="add-user-phone"
                        className="form-label fw-semibold small"
                      >
                        Phone Number
                      </label>
                      <input
                        id="add-user-phone"
                        className="form-control"
                        value={selectedUser.phoneNumber || ""}
                        readOnly={!isNewRecord}
                        onChange={(e) =>
                          setSelectedUser((p) => ({
                            ...p,
                            phoneNumber: e.target.value,
                          }))
                        }
                        style={{ borderRadius: "0.375rem" }}
                      />
                    </div>
                  </div>

                  <GrantAccessForm
                    user={selectedUser}
                    isNewRecord={isNewRecord}
                    onUserOrgChange={(userOrg) => setPendingUserOrg(userOrg)}
                    leaid={leaid}
                    coreData={coreData}
                    entityResult={entityResult}
                    childrenResult={childrenResult}
                    onSaveSuccess={() =>
                      handleSaveSuccess(selectedUser.userName ?? "")
                    }
                    onRegisterSubmit={(fn) => {
                      if (!isNewRecord) submitTriggerRef.current = fn;
                    }}
                    onDirtyChange={(dirty) => setIsFormDirty(dirty)}
                    onSubmittingChange={(submitting) => {
                      if (!isClosingRef.current) setIsSubmitting(submitting);
                    }}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </WizardPopup>

      {showExitConfirm && (
        <ConfirmModal
          titleId="exit-confirm-title"
          title="Are you sure you want to exit?"
          message="All unsaved data will be lost. This action cannot be undone."
          cancelLabel="Continue Editing"
          confirmLabel="Exit"
          onCancel={() => setShowExitConfirm(false)}
          onConfirm={() => {
            setShowExitConfirm(false);
            handleCancel();
          }}
        />
      )}

      {showBackToSearchConfirm && (
        <ConfirmModal
          titleId="back-to-search-title"
          title="Return to Search?"
          message="Going back to search will clear all entered data. This action cannot be undone."
          cancelLabel="Continue Editing"
          confirmLabel="Return to Search"
          onCancel={() => setShowBackToSearchConfirm(false)}
          onConfirm={() => {
            setShowBackToSearchConfirm(false);
            resetWizard();
          }}
        />
      )}
    </>
  );
}
